// Weather Alert System
// Emergency tasks and alerts for garden protection

export type WeatherAlertType = 'frost' | 'freeze' | 'heat-wave' | 'drought' | 'storm' | 'high-wind' | 'hail' | 'flood'

export interface WeatherAlert {
  id: string
  type: WeatherAlertType
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  description: string
  temperature?: number // For frost/freeze/heat alerts
  date: Date
  tasks: EmergencyTask[]
}

export interface EmergencyTask {
  id: string
  task: string
  priority: 'urgent' | 'important' | 'recommended'
  completed: boolean
  instructions: string
  materials?: string[]
  timeEstimate?: string
}

// Alert templates based on weather conditions
export const alertTemplates: Record<WeatherAlertType, (temp?: number, date?: Date) => Omit<WeatherAlert, 'id' | 'date'>> = {
  'frost': (temp, date) => ({
    type: 'frost',
    severity: temp && temp <= 28 ? 'critical' : temp && temp <= 32 ? 'high' : 'medium',
    title: '🥶 Frost Warning',
    description: `Temperatures dropping to ${temp}°F tonight. Protect sensitive plants.`,
    temperature: temp,
    tasks: [
      {
        id: 'frost-1',
        task: 'Cover tender plants',
        priority: 'urgent',
        completed: false,
        instructions: 'Cover tomatoes, peppers, squash, and other tender plants with frost cloth, blankets, or buckets. Remove covers in morning when temps rise above 35°F.',
        materials: ['Frost cloth or old blankets', 'Buckets or pots', 'Clothespins or rocks to secure'],
        timeEstimate: '30-60 minutes'
      },
      {
        id: 'frost-2',
        task: 'Water soil deeply',
        priority: 'important',
        completed: false,
        instructions: 'Water garden thoroughly before sunset. Moist soil holds heat better than dry soil and releases it slowly overnight.',
        materials: ['Hose or watering can'],
        timeEstimate: '20-30 minutes'
      },
      {
        id: 'frost-3',
        task: 'Move container plants indoors',
        priority: 'urgent',
        completed: false,
        instructions: 'Bring potted tomatoes, peppers, herbs, and flowers into garage, shed, or house. Group together for warmth.',
        materials: ['Wheelbarrow or cart (optional)'],
        timeEstimate: '15-30 minutes'
      },
      {
        id: 'frost-4',
        task: 'Harvest ripe produce',
        priority: 'important',
        completed: false,
        instructions: 'Pick all ripe or nearly-ripe tomatoes, peppers, eggplant, and squash. They\'ll be damaged by frost.',
        materials: ['Harvest basket', 'Pruning shears'],
        timeEstimate: '20-40 minutes'
      },
      {
        id: 'frost-5',
        task: 'Set up heat sources',
        priority: 'recommended',
        completed: false,
        instructions: 'For valuable plants, place jugs of hot water or Christmas lights under covers for extra warmth.',
        materials: ['Milk jugs filled with hot water', 'Christmas lights (outdoor rated)'],
        timeEstimate: '15-20 minutes'
      }
    ]
  }),

  'freeze': (temp, date) => ({
    type: 'freeze',
    severity: 'critical',
    title: '❄️ Hard Freeze Warning',
    description: `Temperatures dropping to ${temp}°F. Severe damage likely to unprotected plants.`,
    temperature: temp,
    tasks: [
      {
        id: 'freeze-1',
        task: 'EMERGENCY: Cover everything now!',
        priority: 'urgent',
        completed: false,
        instructions: 'Use anything available - bedsheets, tarps, cardboard boxes, trash bags. Secure edges so wind doesn\'t blow covers off. Priority: tomatoes, peppers, squash, cucumbers.',
        materials: ['Any covering material', 'Rocks, bricks, or stakes to secure'],
        timeEstimate: '45-90 minutes'
      },
      {
        id: 'freeze-2',
        task: 'Harvest everything possible',
        priority: 'urgent',
        completed: false,
        instructions: 'Pick ALL tomatoes (even green), peppers, squash, cucumbers, beans. Green tomatoes will ripen indoors.',
        materials: ['Large baskets or boxes', 'Pruning shears'],
        timeEstimate: '30-60 minutes'
      },
      {
        id: 'freeze-3',
        task: 'Move all containers to shelter',
        priority: 'urgent',
        completed: false,
        instructions: 'Get every potted plant into garage, basement, or house. Even "cold hardy" plants can be damaged in hard freeze.',
        materials: ['Wheelbarrow', 'Tarps to protect floors'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'freeze-4',
        task: 'Water garden heavily',
        priority: 'important',
        completed: false,
        instructions: 'Saturate soil around all plants. Wet soil insulates roots and releases heat as it freezes.',
        materials: ['Sprinkler or hose'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'freeze-5',
        task: 'Set up emergency heat',
        priority: 'important',
        completed: false,
        instructions: 'Place outdoor Christmas lights, heat lamps, or even candles (safely!) under covers for critical plants.',
        materials: ['Outdoor extension cords', 'Christmas lights or heat lamps', 'Candles in safe holders (emergency only)'],
        timeEstimate: '20-30 minutes'
      }
    ]
  }),

  'heat-wave': (temp, date) => ({
    type: 'heat-wave',
    severity: temp && temp >= 100 ? 'critical' : temp && temp >= 95 ? 'high' : 'medium',
    title: '🌡️ Extreme Heat Warning',
    description: `Temperatures reaching ${temp}°F. Plants at risk of heat stress and sunscald.`,
    temperature: temp,
    tasks: [
      {
        id: 'heat-1',
        task: 'Water deeply in early morning',
        priority: 'urgent',
        completed: false,
        instructions: 'Water thoroughly before 8 AM. Soak soil to 6-8 inches deep. Avoid wetting leaves to prevent sunscald.',
        materials: ['Soaker hose or drip irrigation', 'Watering can with long spout'],
        timeEstimate: '45-60 minutes'
      },
      {
        id: 'heat-2',
        task: 'Provide shade for sensitive plants',
        priority: 'important',
        completed: false,
        instructions: 'Erect shade cloth (30-50%) over tomatoes, peppers, lettuce, and herbs. Use stakes, hoops, or clotheslines.',
        materials: ['Shade cloth', 'Stakes or hoops', 'Clothespins or clips'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'heat-3',
        task: 'Mulch to retain moisture',
        priority: 'important',
        completed: false,
        instructions: 'Add 2-3 inches of straw, wood chips, or shredded leaves around plants. Keep mulch away from stems.',
        materials: ['Straw, wood chips, or leaves', 'Wheelbarrow', 'Rake or hands'],
        timeEstimate: '60-90 minutes'
      },
      {
        id: 'heat-4',
        task: 'Harvest heat-sensitive crops',
        priority: 'important',
        completed: false,
        instructions: 'Pick lettuce, spinach, cilantro, and other cool-weather crops before they bolt or turn bitter.',
        materials: ['Harvest basket', 'Pruning shears'],
        timeEstimate: '20-30 minutes'
      },
      {
        id: 'heat-5',
        task: 'Set up drip irrigation or timers',
        priority: 'recommended',
        completed: false,
        instructions: 'Install soaker hoses or drip lines with timers for consistent watering during heat wave.',
        materials: ['Soaker hoses or drip tubing', 'Timer', 'Hose connectors'],
        timeEstimate: '45-60 minutes'
      }
    ]
  }),

  'drought': () => ({
    type: 'drought',
    severity: 'high',
    title: '💧 Drought Conditions',
    description: 'Extended dry period. Water conservation critical for plant survival.',
    tasks: [
      {
        id: 'drought-1',
        task: 'Water deeply and less frequently',
        priority: 'urgent',
        completed: false,
        instructions: 'Water to 8-10 inches deep every 3-5 days instead of shallow daily watering. Encourages deep roots.',
        materials: ['Soaker hoses', 'Moisture meter (optional)'],
        timeEstimate: '60-90 minutes'
      },
      {
        id: 'drought-2',
        task: 'Apply thick mulch layer',
        priority: 'urgent',
        completed: false,
        instructions: 'Add 4-6 inches of organic mulch (straw, wood chips, leaves) to reduce evaporation by 50-70%.',
        materials: ['Mulch material', 'Wheelbarrow'],
        timeEstimate: '2-3 hours'
      },
      {
        id: 'drought-3',
        task: 'Prioritize watering',
        priority: 'important',
        completed: false,
        instructions: 'Focus on: 1) Newly planted/transplanted, 2) Flowering/fruiting plants, 3) Container plants. Let established perennials go dormant if needed.',
        materials: ['None'],
        timeEstimate: 'Ongoing'
      },
      {
        id: 'drought-4',
        task: 'Collect and reuse water',
        priority: 'important',
        completed: false,
        instructions: 'Place buckets in showers, use dishwater, install rain barrels. Every gallon counts.',
        materials: ['Buckets', 'Rain barrel (if available)'],
        timeEstimate: 'Setup: 30 minutes'
      },
      {
        id: 'drought-5',
        task: 'Stop fertilizing',
        priority: 'recommended',
        completed: false,
        instructions: 'Fertilizer encourages growth which increases water needs. Wait until drought ends.',
        materials: ['None'],
        timeEstimate: 'Immediate'
      }
    ]
  }),

  'storm': () => ({
    type: 'storm',
    severity: 'high',
    title: '⛈️ Severe Storm Warning',
    description: 'Heavy rain, strong winds, and potential hail expected. Protect plants and harvest ripe produce.',
    tasks: [
      {
        id: 'storm-1',
        task: 'Harvest all ripe and nearly-ripe produce',
        priority: 'urgent',
        completed: false,
        instructions: 'Pick tomatoes, peppers, squash, berries, and anything that could be damaged by rain or wind.',
        materials: ['Harvest baskets', 'Pruning shears'],
        timeEstimate: '30-60 minutes'
      },
      {
        id: 'storm-2',
        task: 'Secure tall plants and trellises',
        priority: 'urgent',
        completed: false,
        instructions: 'Stake tomatoes, secure corn, reinforce trellises. High winds can uproot or break plants.',
        materials: ['Stakes', 'Twine or plant ties', 'Mallet'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'storm-3',
        task: 'Move container plants to shelter',
        priority: 'important',
        completed: false,
        instructions: 'Bring pots into garage, against house, or huddle together in protected area. Turn fragile pots on side.',
        materials: ['Wheelbarrow (optional)'],
        timeEstimate: '20-30 minutes'
      },
      {
        id: 'storm-4',
        task: 'Cover vulnerable plants',
        priority: 'important',
        completed: false,
        instructions: 'Use row cover, netting, or sheets to protect from hail damage. Secure well against wind.',
        materials: ['Row cover or sheets', 'Stakes', 'Clothespins'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'storm-5',
        task: 'Ensure drainage is clear',
        priority: 'recommended',
        completed: false,
        instructions: 'Clear drains, gutters, and low spots where water pools. Raised beds should be OK but watch for flooding.',
        materials: ['Shovel', 'Rake'],
        timeEstimate: '20-30 minutes'
      }
    ]
  }),

  'high-wind': () => ({
    type: 'high-wind',
    severity: 'medium',
    title: '💨 High Wind Warning',
    description: 'Sustained winds of 25+ mph expected. Secure plants and structures.',
    tasks: [
      {
        id: 'wind-1',
        task: 'Stake and tie tall plants',
        priority: 'urgent',
        completed: false,
        instructions: 'Secure tomatoes, peppers, corn, sunflowers with stakes and soft ties. Create windbreaks if possible.',
        materials: ['Stakes', 'Soft plant ties or strips of cloth', 'Mallet'],
        timeEstimate: '45-60 minutes'
      },
      {
        id: 'wind-2',
        task: 'Reinforce trellises and cages',
        priority: 'urgent',
        completed: false,
        instructions: 'Check all supports, add extra stakes, tighten ties. Wind can topple heavy cages.',
        materials: ['Extra stakes', 'Twine or wire'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'wind-3',
        task: 'Move or secure containers',
        priority: 'important',
        completed: false,
        instructions: 'Cluster pots together against house or fence. Lay lightweight pots on side. Weight down with bricks.',
        materials: ['Bricks or rocks'],
        timeEstimate: '20-30 minutes'
      },
      {
        id: 'wind-4',
        task: 'Remove or secure row covers',
        priority: 'important',
        completed: false,
        instructions: 'Take off lightweight covers that could blow away, or secure edges with heavy objects every 2 feet.',
        materials: ['Rocks, bricks, or sandbags'],
        timeEstimate: '15-25 minutes'
      },
      {
        id: 'wind-5',
        task: 'Harvest vulnerable crops',
        priority: 'recommended',
        completed: false,
        instructions: 'Pick anything that might blow off plants or be damaged by rubbing - corn, tall herbs, flowers.',
        materials: ['Harvest basket'],
        timeEstimate: '20-30 minutes'
      }
    ]
  }),

  'hail': () => ({
    type: 'hail',
    severity: 'critical',
    title: '🧊 Hail Warning',
    description: 'Hail expected! Immediate action needed to prevent catastrophic damage.',
    tasks: [
      {
        id: 'hail-1',
        task: 'EMERGENCY: Cover everything NOW!',
        priority: 'urgent',
        completed: false,
        instructions: 'Use anything heavy-duty: plywood, thick blankets, tarps with supports. Priority: fruit, tomatoes, peppers.',
        materials: ['Plywood, tarps, or thick blankets', 'Bricks or weights', 'Stakes for support'],
        timeEstimate: '30-60 minutes'
      },
      {
        id: 'hail-2',
        task: 'Move containers to complete shelter',
        priority: 'urgent',
        completed: false,
        instructions: 'Get ALL pots into garage, shed, or house. Even "protected" porches may not stop hail.',
        materials: ['Wheelbarrow'],
        timeEstimate: '20-30 minutes'
      },
      {
        id: 'hail-3',
        task: 'Harvest everything valuable',
        priority: 'urgent',
        completed: false,
        instructions: 'Pick all ripe produce, cut flowers, anything you can\'t afford to lose. Hail destroys in minutes.',
        materials: ['Baskets', 'Pruning shears'],
        timeEstimate: '20-40 minutes'
      },
      {
        id: 'hail-4',
        task: 'Create emergency structures',
        priority: 'important',
        completed: false,
        instructions: 'Lean plywood against fence for tomatoes, overturn laundry baskets weighted with rocks. Be creative!',
        materials: ['Plywood, pallets, or boards', 'Rocks or bricks', 'Laundry baskets'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'hail-5',
        task: 'Check insurance and document garden',
        priority: 'recommended',
        completed: false,
        instructions: 'Take photos of garden before storm for insurance claims. Note mature plants and yields.',
        materials: ['Phone/camera'],
        timeEstimate: '10-15 minutes'
      }
    ]
  }),

  'flood': () => ({
    type: 'flood',
    severity: 'critical',
    title: '🌊 Flood Warning',
    description: 'Heavy rain and flooding expected. Protect plants from drowning and root rot.',
    tasks: [
      {
        id: 'flood-1',
        task: 'Create drainage channels',
        priority: 'urgent',
        completed: false,
        instructions: 'Dig trenches to divert water away from garden. Clear existing drains and gutters.',
        materials: ['Shovel', 'Rake'],
        timeEstimate: '45-90 minutes'
      },
      {
        id: 'flood-2',
        task: 'Move containers to high ground',
        priority: 'urgent',
        completed: false,
        instructions: 'Get all pots onto tables, porches, or any elevated surface. Even 6 inches helps.',
        materials: ['Tables, blocks, or pallets'],
        timeEstimate: '30-45 minutes'
      },
      {
        id: 'flood-3',
        task: 'Harvest root vegetables',
        priority: 'important',
        completed: false,
        instructions: 'Dig potatoes, carrots, beets that could rot in saturated soil. Better early than rotten.',
        materials: ['Garden fork', 'Harvest basket'],
        timeEstimate: '30-60 minutes'
      },
      {
        id: 'flood-4',
        task: 'Create raised barriers',
        priority: 'important',
        completed: false,
        instructions: 'Pile soil, sandbags, or mulch around base of valuable plants to create mini-berms.',
        materials: ['Soil or sandbags', 'Mulch'],
        timeEstimate: '45-60 minutes'
      },
      {
        id: 'flood-5',
        task: 'Document for insurance',
        priority: 'recommended',
        completed: false,
        instructions: 'Take photos/video of garden before flood. Note plant varieties, ages, and expected yields.',
        materials: ['Phone/camera'],
        timeEstimate: '15-20 minutes'
      }
    ]
  })
}

// Generate a unique alert ID
export const generateAlertId = () => `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Create a new weather alert
export const createWeatherAlert = (
  type: WeatherAlertType,
  temperature?: number,
  customDate?: Date
): WeatherAlert => {
  const template = alertTemplates[type](temperature, customDate)
  return {
    id: generateAlertId(),
    date: customDate || new Date(),
    ...template,
    tasks: template.tasks.map(task => ({
      ...task,
      id: `${task.id}-${Date.now()}`
    }))
  }
}

// Check if conditions warrant an alert
export const shouldCreateAlert = (
  type: WeatherAlertType,
  temperature?: number,
  hasExistingAlert: boolean = false
): boolean => {
  if (hasExistingAlert) return false // Don't duplicate alerts
  
  switch (type) {
    case 'frost':
      return temperature !== undefined && temperature <= 35
    case 'freeze':
      return temperature !== undefined && temperature <= 28
    case 'heat-wave':
      return temperature !== undefined && temperature >= 90
    case 'drought':
      return true // Based on extended forecast, not single temp
    case 'storm':
    case 'high-wind':
    case 'hail':
    case 'flood':
      return true // Based on weather service warnings
    default:
      return false
  }
}

// Get priority color for UI
export const getSeverityColor = (severity: WeatherAlert['severity']): string => {
  switch (severity) {
    case 'critical': return '#d32f2f' // Red
    case 'high': return '#f57c00' // Orange
    case 'medium': return '#fbc02d' // Yellow
    case 'low': return '#388e3c' // Green
    default: return '#757575' // Gray
  }
}

// Get priority label
export const getSeverityLabel = (severity: WeatherAlert['severity']): string => {
  switch (severity) {
    case 'critical': return 'CRITICAL'
    case 'high': return 'HIGH'
    case 'medium': return 'MEDIUM'
    case 'low': return 'LOW'
    default: return 'UNKNOWN'
  }
}

// Sample alerts for testing
export const sampleAlerts: WeatherAlert[] = [
  createWeatherAlert('frost', 30),
  createWeatherAlert('heat-wave', 98),
  createWeatherAlert('storm')
]
